import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type AdvertisableProduct, type AdvertRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSellerPharmacy } from '@/lib/onboardingQueries';

/**
 * A pharmacy buying its own advertising.
 *
 * ── WHAT THIS SCREEN PROMISES, AND WHAT IT MUST NOT ─────────────────────────
 *
 * It sells visibility. It does not sell a better price position, and saying so
 * on the screen matters more than saying it in a commit message — the person
 * paying is the one who will eventually ask. The offer ladder on a product page
 * is ordered by what the buyer pays and says so; nothing bought here moves it.
 *
 * ── WHY PRESCRIPTION-ONLY MEDICINES ARE SIMPLY ABSENT ───────────────────────
 *
 * §9.7. The product picker is served by the API and never contains one, so a
 * seller does not meet a refusal — they meet a list that does not include it.
 * Being told "no" by a form is how somebody learns a rule exists and starts
 * looking for the way around it.
 */
export function AdvertsScreen() {
  const [buying, setBuying] = useState(false);

  if (buying) {
    return <BuyAdvert onDone={() => setBuying(false)} />;
  }

  return <MyAdverts onBuy={() => setBuying(true)} />;
}

// ── What this shop has bought ──────────────────────────────────────────────

function MyAdverts({ onBuy }: { onBuy: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['seller', 'adverts'],
    queryFn: ({ signal }) => api.myAdverts(signal),
  });

  const pay = useMutation({
    mutationFn: (reference: string) => api.payForAdvert(reference),
    onSuccess: (result) => {
      if (result.authorization_url) {
        // Same tab. A payment page opened in a new tab on an Android browser
        // is a payment page somebody loses.
        window.location.href = result.authorization_url;
      }
    },
  });

  const confirm = useMutation({
    mutationFn: (reference: string) => api.confirmAdvert(reference),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller', 'adverts'] }),
  });

  if (isLoading) {
    return (
      <div className="space-y-2.5 p-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-[84px] w-full" radius="card" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4">
        <EmptyState
          title="We couldn't load your advertising"
          body="This is usually a connection problem."
          action={<Button onClick={() => refetch()}>Try again</Button>}
        />
      </div>
    );
  }

  const adverts = data ?? [];

  return (
    <div className="mx-auto w-full max-w-[900px] px-4 py-5 xl:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">Advertising</h1>
          <p className="m-0 mt-1 max-w-[58ch] text-[12.5px] leading-relaxed text-muted">
            Put your shop, or one product, in front of more buyers. It does not change where you
            appear on price — that list is ordered cheapest first and says so on the page.
          </p>
        </div>

        <Button onClick={onBuy} data-testid="advert-new">
          Advertise something
        </Button>
      </div>

      {adverts.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="Nothing running"
            body="A placement puts your shop or a product in a labelled block on the home screen, in search, or on a category shelf."
            action={<Button onClick={onBuy}>See what it costs</Button>}
          />
        </div>
      ) : (
        <div className="mt-5 space-y-2.5">
          {adverts.map((advert) => (
            <AdvertCard
              key={advert.reference}
              advert={advert}
              busy={pay.isPending || confirm.isPending}
              onPay={() => pay.mutate(advert.reference)}
              onConfirm={() => confirm.mutate(advert.reference)}
              error={
                confirm.error instanceof ApiError && confirm.variables === advert.reference
                  ? confirm.error.message
                  : null
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AdvertCard({
  advert,
  busy,
  onPay,
  onConfirm,
  error,
}: {
  advert: AdvertRow;
  busy: boolean;
  onPay: () => void;
  onConfirm: () => void;
  error: string | null;
}) {
  const unpaid = advert.status === 'pending_payment';

  return (
    <div
      data-testid={`advert-${advert.reference}`}
      className="rounded-card border border-line bg-white p-3.5"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="m-0 text-[13.5px] font-semibold text-ink">
            {advert.kind === 'product' ? (advert.product ?? 'A product') : 'Your shop'}
          </p>
          <p className="m-0 mt-0.5 text-[12px] text-muted">
            {advert.placement_label} · {advert.units} {advert.unit}
            {advert.units === 1 ? '' : 's'} · {advert.starts_on} to {advert.ends_on}
          </p>
        </div>

        <Status advert={advert} />
      </div>

      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-line-2 pt-2.5">
        <span className="font-mono text-[13px] font-bold text-ink">{advert.total_display}</span>

        {unpaid ? (
          <div className="flex flex-wrap gap-2">
            <Button onClick={onPay} loading={busy} data-testid={`pay-${advert.reference}`}>
              Pay now
            </Button>
            {/* The way back for somebody who paid and closed the tab before
                being returned. Without it their money is gone and their advert
                is not running, and the only fix is a support call. */}
            {advert.payment_reference ? (
              <Button variant="ghost" onClick={onConfirm} loading={busy}>
                I have paid
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? <p className="m-0 mt-2 text-[11.5px] font-medium text-danger">{error}</p> : null}
    </div>
  );
}

function Status({ advert }: { advert: AdvertRow }) {
  if (advert.is_live) {
    return (
      <span className="flex shrink-0 items-center gap-1 rounded-pill bg-verified-tint px-2 py-0.5 text-[11px] font-semibold text-verified">
        <Icon name="check" size={11} strokeWidth={2.6} />
        Running
      </span>
    );
  }

  const label =
    advert.status === 'pending_payment'
      ? 'Waiting for payment'
      : advert.status === 'cancelled'
        ? 'Cancelled'
        : new Date(advert.starts_on) > new Date()
          ? 'Starts soon'
          : 'Finished';

  return (
    <span className="shrink-0 rounded-pill bg-paper px-2 py-0.5 text-[11px] font-semibold text-muted">
      {label}
    </span>
  );
}

// ── Buying one ─────────────────────────────────────────────────────────────

const PLACEMENTS = ['home', 'search', 'category'] as const;
const UNITS = ['day', 'week', 'month', 'year'] as const;

function BuyAdvert({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  // The hook returns the whole query; the shelves live on the pharmacy it
  // carries. A seller approved for one shelf never sees the choice at all.
  const { data: seller } = useSellerPharmacy();
  const sectors: string[] = seller?.pharmacy?.sectors ?? ['human'];

  const [kind, setKind] = useState<'shop' | 'product'>('product');
  const [sector, setSector] = useState<string>(sectors[0] ?? 'human');
  const [placement, setPlacement] = useState<(typeof PLACEMENTS)[number]>('search');
  const [unit, setUnit] = useState<(typeof UNITS)[number]>('week');
  const [units, setUnits] = useState(2);
  const [product, setProduct] = useState<AdvertisableProduct | null>(null);
  const [search, setSearch] = useState('');

  const { data: rates } = useQuery({
    queryKey: ['seller', 'advert-rates'],
    queryFn: ({ signal }) => api.advertRates(signal),
  });

  const { data: products, isLoading: loadingProducts } = useQuery({
    queryKey: ['seller', 'advertisable', sector, search],
    queryFn: ({ signal }) => api.advertisableProducts(sector, search || undefined, signal),
    enabled: kind === 'product',
  });

  // Priced from the card rather than multiplied in the browser. A number a
  // seller is asked to pay must come from the same place the invoice does.
  const { data: quote, isFetching: pricing } = useQuery({
    queryKey: ['seller', 'advert-quote', kind, placement, sector, unit, units],
    queryFn: () => api.quoteAdvert({ kind, placement, sector, unit, units }),
    retry: false,
  });

  const buy = useMutation({
    mutationFn: () =>
      api.buyAdvert({
        kind,
        placement,
        sector,
        unit,
        units,
        product_id: kind === 'product' ? product?.id : null,
      }),
    onSuccess: async (advert) => {
      queryClient.invalidateQueries({ queryKey: ['seller', 'adverts'] });

      // Straight to payment. An advert created and then left unpaid on a list
      // is an advert the seller believes is running.
      const payment = await api.payForAdvert(advert.reference).catch(() => null);

      if (payment?.authorization_url) {
        window.location.href = payment.authorization_url;

        return;
      }

      onDone();
    },
  });

  const available = useMemo(
    () => (rates ?? []).filter((rate) => rate.kind === kind && rate.sector === sector),
    [rates, kind, sector],
  );

  const errors = buy.error instanceof ApiError ? buy.error.fieldErrors : {};
  const ready = kind === 'shop' || product !== null;

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-5 xl:px-6">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 flex min-h-9 items-center gap-1.5 text-[12.5px] font-semibold text-muted hover:text-blue"
      >
        <Icon name="back" size={14} strokeWidth={2.2} />
        Advertising
      </button>

      <h1 className="m-0 font-display text-[21px] font-bold tracking-[-0.02em]">
        Advertise something
      </h1>

      {/* Said here, where the person paying reads it, not only in the code.
          They are the ones who will eventually ask. */}
      <p className="m-0 mt-1.5 max-w-[60ch] rounded-card border border-line bg-paper px-3 py-2.5 text-[12px] leading-relaxed text-muted">
        <b className="font-semibold text-ink">What this buys:</b> a labelled block where more
        buyers see you. <b className="font-semibold text-ink">What it does not:</b> a better
        position on price. Every product page lists offers cheapest first and tells buyers so —
        advertising never changes that order.
      </p>

      <form
        className="mt-5 space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          buy.mutate();
        }}
      >
        <Choice
          label="What are you advertising?"
          value={kind}
          onChange={(next) => {
            setKind(next as 'shop' | 'product');
            setProduct(null);
          }}
          options={[
            { value: 'product', label: 'One product', hint: 'Something that is moving' },
            { value: 'shop', label: 'My whole shop', hint: 'Your name and your shelf' },
          ]}
        />

        {sectors.length > 1 ? (
          <Choice
            label="Which shelf?"
            value={sector}
            onChange={setSector}
            options={sectors.map((one: string) => ({
              value: one,
              label: one === 'veterinary' ? 'Animal health' : 'Human medicines',
            }))}
          />
        ) : null}

        {kind === 'product' ? (
          <div>
            <p className="m-0 mb-[5px] text-[11px] font-bold tracking-[0.01em] text-muted">
              Which product?
            </p>

            <Input
              label=""
              placeholder="Search your products"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            <div
              className="mt-2 max-h-[220px] overflow-y-auto rounded-card border border-line"
              data-testid="advert-product-list"
            >
              {loadingProducts ? (
                <p className="m-0 p-3 text-[12px] text-faint">Loading your products…</p>
              ) : (products ?? []).length === 0 ? (
                <p className="m-0 p-3 text-[12px] leading-relaxed text-faint">
                  Nothing here you can advertise. You can only advertise products you are
                  currently selling, and prescription-only medicines can never be advertised.
                </p>
              ) : (
                (products ?? []).map((one) => (
                  <button
                    key={one.id}
                    type="button"
                    onClick={() => setProduct(one)}
                    className={cn(
                      'flex min-h-11 w-full items-center justify-between gap-2 border-b border-line-2 px-3 text-left text-[12.5px] last:border-b-0',
                      product?.id === one.id ? 'bg-blue-tint font-semibold text-blue' : 'text-ink',
                    )}
                  >
                    <span className="min-w-0 truncate">{one.name}</span>
                    {product?.id === one.id ? (
                      <Icon name="check" size={14} strokeWidth={2.6} className="shrink-0" />
                    ) : null}
                  </button>
                ))
              )}
            </div>

            {errors.product_id ? (
              <p className="m-0 mt-1.5 text-[11px] font-medium text-danger">
                {errors.product_id[0]}
              </p>
            ) : null}

            <p className="m-0 mt-1.5 text-[11px] leading-snug text-faint">
              Prescription-only and controlled medicines are never on this list. That is a rule,
              not a setting.
            </p>
          </div>
        ) : null}

        <Choice
          label="Where should it show?"
          value={placement}
          onChange={(next) => setPlacement(next as (typeof PLACEMENTS)[number])}
          options={PLACEMENTS.map((one) => {
            const rate = available.find((r) => r.placement === one);

            return {
              value: one,
              label: rate?.placement_label ?? one,
              hint: rate?.placement_blurb,
            };
          })}
        />

        <div>
          <p className="m-0 mb-[5px] text-[11px] font-bold tracking-[0.01em] text-muted">
            For how long?
          </p>
          <div className="flex flex-wrap gap-2">
            {UNITS.map((one) => (
              <button
                key={one}
                type="button"
                onClick={() => setUnit(one)}
                className={cn(
                  'min-h-11 rounded-btn border px-3 text-[12.5px] font-semibold capitalize',
                  unit === one
                    ? 'border-blue bg-blue-tint text-blue'
                    : 'border-line bg-white text-muted hover:text-ink',
                )}
              >
                {one}
              </button>
            ))}
          </div>

          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              aria-label="Fewer"
              onClick={() => setUnits((n) => Math.max(1, n - 1))}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-btn border border-line bg-white text-ink"
            >
              <Icon name="minus" size={15} strokeWidth={2.4} />
            </button>
            <span className="min-w-[90px] text-center text-[13.5px] font-semibold text-ink">
              {units} {unit}
              {units === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              aria-label="More"
              onClick={() => setUnits((n) => Math.min(52, n + 1))}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-btn border border-line bg-white text-ink"
            >
              <Icon name="plus" size={15} strokeWidth={2.4} />
            </button>
          </div>
        </div>

        {/* The price, from the server. Never multiplied in the browser: the
            number somebody is asked to pay has to come from the same place the
            invoice does. */}
        <div
          className="rounded-card border border-line bg-paper p-3.5"
          data-testid="advert-quote"
          aria-live="polite"
        >
          {quote ? (
            <>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[12.5px] text-muted">
                  {quote.days} day{quote.days === 1 ? '' : 's'}, {quote.starts_on} to{' '}
                  {quote.ends_on}
                </span>
                <span className="font-mono text-[19px] font-bold text-ink">
                  {quote.total_display}
                </span>
              </div>
              <p className="m-0 mt-1 text-[11px] text-faint">
                {quote.unit_price_display} per {quote.unit}
                {pricing ? ' · updating…' : ''}
              </p>
            </>
          ) : (
            <p className="m-0 text-[12.5px] text-faint">
              That length is not on sale for this placement. Try another.
            </p>
          )}
        </div>

        {buy.error instanceof ApiError && !buy.error.isValidation ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-danger/25 bg-danger-tint px-3 py-2.5 text-[12px] text-danger"
          >
            <Icon name="alert" size={14} className="mt-px shrink-0" />
            <span>{buy.error.message}</span>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={buy.isPending} disabled={!ready || !quote}>
            {quote ? `Pay ${quote.total_display}` : 'Choose a length'}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>

        <p className="m-0 text-[11px] leading-relaxed text-faint">
          You pay by card on Paystack. Nothing shows to buyers until the payment has gone through.
        </p>
      </form>
    </div>
  );
}

function Choice({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; hint?: string }>;
}) {
  return (
    <div>
      <p className="m-0 mb-[5px] text-[11px] font-bold tracking-[0.01em] text-muted">{label}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={value === option.value}
            className={cn(
              'rounded-card border p-3 text-left',
              value === option.value
                ? 'border-blue bg-blue-tint'
                : 'border-line bg-white hover:border-line',
            )}
          >
            <span
              className={cn(
                'block text-[13px] font-semibold',
                value === option.value ? 'text-blue' : 'text-ink',
              )}
            >
              {option.label}
            </span>
            {option.hint ? (
              <span className="mt-0.5 block text-[11px] leading-snug text-muted">{option.hint}</span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
